import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Wrench, ArrowLeft, TrendingUp, Clock, Users, Package, CheckCircle } from 'lucide-react';

const AutoPartsCaseStudy = () => {
  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <article className="pt-40 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/industries" className="inline-flex items-center gap-2 text-[#2563EB] hover:text-[#3B82F6] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Industries
          </Link>
          
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-500 to-gray-600 rounded-xl flex items-center justify-center">
                <Wrench className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="text-sm text-[#2563EB] font-semibold uppercase tracking-wide">
                  Auto Services & Parts
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#F8FAFC]">
                  Midwest Auto Supply
                </h1>
              </div>
            </div>
            <p className="text-xl text-[#94A3B8]">
              How an auto parts retailer achieved 35% faster inventory turnover and cleared $50K in dead stock
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden mb-12 border border-[#2563EB]/20">
            <img
              src="https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=1200"
              alt="Midwest Auto Supply Parts Store"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C]/60 to-transparent" />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Package className="w-8 h-8 text-[#2563EB] mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">4,200</div>
              <div className="text-[#94A3B8]">SKUs in Inventory</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Users className="w-8 h-8 text-green-400 mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">45</div>
              <div className="text-[#94A3B8]">Professional Shop Accounts</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Clock className="w-8 h-8 text-gray-400 mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">22 years</div>
              <div className="text-[#94A3B8]">Serving the Community</div>
            </div>
          </div>

          <div className="prose prose-invert max-w-none">
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-4">The Challenge</h2>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-4">
                Midwest Auto Supply serves two very different customer types: DIY car enthusiasts who walk in for parts, and professional mechanics who need wholesale pricing and fast delivery. Managing pricing for both audiences across 4,200 SKUs was becoming impossible.
              </p>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-4">
                Their biggest headache? Dead inventory. Parts for older vehicle models sat on shelves for years, tying up capital. Meanwhile, high-demand items were priced too low because "that's what we've always charged." The owner knew they were leaving money on the table but had no idea where to start.
              </p>
              <p className="text-[#94A3B8] text-lg leading-relaxed">
                On top of that, their professional shop accounts expected discounts, but there was no systematic way to manage who got what pricing—leading to inconsistent quotes and frustrated customers.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-4">The Solution</h2>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-6">
                PriceStrike helped Midwest Auto Supply implement intelligent pricing across their entire operation:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Dual-Pricing Engine:</span>
                    <span className="text-[#94A3B8]"> Automatic retail vs. wholesale pricing based on customer type, with shop accounts getting their negotiated discounts applied instantly.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Slow-Mover Detection:</span>
                    <span className="text-[#94A3B8]"> AI identified 340 SKUs that hadn't moved in 6+ months and recommended markdown strategies to clear them.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Demand-Based Repricing:</span>
                    <span className="text-[#94A3B8]"> High-velocity items automatically adjusted to capture full margin potential based on local demand patterns.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <span className="text-[#F8FAFC] font-semibold">Competitive Intelligence:</span>
                    <span className="text-[#94A3B8]"> Monitoring of AutoZone and O'Reilly pricing on key items to ensure competitive positioning on traffic drivers.</span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-green-500/10 to-[#111827] border border-green-500/30 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold text-[#F8FAFC] mb-6">The Results</h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">35%</div>
                  <div className="text-[#94A3B8]">Faster inventory turnover</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">$50K</div>
                  <div className="text-[#94A3B8]">Dead stock cleared</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">18%</div>
                  <div className="text-[#94A3B8]">Margin improvement on fast-movers</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">100%</div>
                  <div className="text-[#94A3B8]">Shop accounts on automated pricing</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#2563EB]/10 to-[#111827] border border-[#2563EB]/30 rounded-2xl p-8 mb-12">
              <blockquote className="text-xl text-[#F8FAFC] italic mb-4">
                "We cleared $50K in dead stock just by finding the right price points. And now my shop accounts know exactly what discount they're getting—no more back-and-forth on every order."
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-gray-500 to-gray-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">TJ</span>
                </div>
                <div>
                  <div className="text-[#F8FAFC] font-semibold">Tom Jensen</div>
                  <div className="text-[#94A3B8]">Owner, Midwest Auto Supply</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Link to="/coming-soon">
              <button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-4 rounded-lg font-semibold transition-colors">
                Start Your Free Trial
              </button>
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default AutoPartsCaseStudy;
